import { Button, Input, Label, Spinner } from '@1trading/react';
import { ClipboardCheck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ReportFilesTable } from '@/features/reports/report-files-table';
import { useReports } from '@/hooks/queries/reports/use-reports';
import { useQueryErrorToast } from '@/hooks/use-query-error-toast';
import { getErrorMessage } from '@/lib/api/error';
import { ACCOUNT_CLEANUP_REPORT_TYPE } from '@/services/api/account-cleanup';
import type { ReportFile, ReportsQuery } from '@/services/api/reports';
import { daysAgo, toDateInputValue } from '@/utils/date';
import { extractFileName } from '@/utils/string';
import { cleanupFileKind } from './cleanup-report';

// Weekly reports, so a couple of months keeps the list short but never empty
const DEFAULT_RANGE_DAYS = 60;
const FETCH_ERROR_TOAST_ID = 'account-cleanup-fetch-failed';
const VIEW_PATHNAME = '/operations/user-audit/cleanup/view';
const REVIEW_PATHNAME = '/operations/user-audit/cleanup/review';

const defaultQuery = (): ReportsQuery => ({
  type: ACCOUNT_CLEANUP_REPORT_TYPE,
  from: toDateInputValue(daysAgo(DEFAULT_RANGE_DAYS)),
  to: toDateInputValue(new Date()),
});

const ReviewCell = ({ fileKey }: { fileKey: string }) => (
  <Button asChild aria-label={`Review ${extractFileName(fileKey)}`} className="gap-2" size="sm" type="button" variant="outline">
    <Link to={{ pathname: REVIEW_PATHNAME, search: `?key=${encodeURIComponent(fileKey)}` }}>
      <ClipboardCheck className="size-4" />
      Review
    </Link>
  </Button>
);
ReviewCell.displayName = 'ReviewCell';

const renderAction = (file: ReportFile) => (cleanupFileKind(file.key) === 'report' ? <ReviewCell fileKey={file.key} /> : null);
const kindOf = (file: ReportFile) => cleanupFileKind(file.key);

/** The weekly cleanup reports and the outcomes of runs against them; reports open into the review screen. */
export const AccountCleanupReports = () => {
  const [draft, setDraft] = useState<ReportsQuery>(defaultQuery);
  // Auto-fetches once on mount with the defaults; afterwards only the Search
  // button moves `submitted`, so edits to the controls never fetch on their own.
  const [submitted, setSubmitted] = useState<ReportsQuery>(draft);

  const reportsQuery = useReports(submitted);
  const { data, isPending, isFetching, isError, error, refetch } = reportsQuery;

  useQueryErrorToast(reportsQuery, 'Failed to fetch account cleanup reports', FETCH_ERROR_TOAST_ID);

  const handleSearch = () => {
    if (draft.from === submitted.from && draft.to === submitted.to) {
      refetch();
      return;
    }
    setSubmitted(draft);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="account-cleanup-from">From</Label>
          <Input
            className="w-44"
            id="account-cleanup-from"
            onChange={(event) => setDraft((previous) => ({ ...previous, from: event.target.value }))}
            type="date"
            value={draft.from}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="account-cleanup-to">To</Label>
          <Input className="w-44" id="account-cleanup-to" onChange={(event) => setDraft((previous) => ({ ...previous, to: event.target.value }))} type="date" value={draft.to} />
        </div>
        <Button className="gap-2" disabled={isFetching} onClick={handleSearch} type="button">
          {isFetching ? <Spinner className="size-4" /> : null}
          Search
        </Button>
      </div>
      {isError ? (
        <div className="flex items-center gap-3">
          <p className="text-sm text-red-500">Failed to fetch account cleanup reports: {getErrorMessage(error)}</p>
          <Button onClick={() => refetch()} size="sm" type="button" variant="outline">
            Retry
          </Button>
        </div>
      ) : null}
      <ReportFilesTable
        emptyMessage="No account cleanup reports found in the selected date range. The report runs weekly."
        files={data?.files}
        isPending={isPending}
        kindOf={kindOf}
        renderAction={renderAction}
        viewPathname={VIEW_PATHNAME}
      />
    </div>
  );
};
AccountCleanupReports.displayName = 'AccountCleanupReports';
